import Controller from '@ember/controller';
import moment from 'moment-timezone';
import { beebeeAjaxGet, beebeeAjaxPost, notifyUserError } from 'beebee-base/lib/utility';

import { computed, set } from '@ember/object';

import { task, timeout } from 'ember-concurrency';

import { inject as iService } from '@ember/service';

import { inject as iController } from '@ember/controller';

import getConfig from 'beebee-base/lib/get-config';
export default class UserController extends Controller {
  @iService intl;
  @iService notify;
  @iService session;
  @iService router;
  @iService store;
  @iController('application') applicationController;

  showTab = null;

  // Self-management state
  selectedEventForReschedule = null;
  showCancelConfirm = null;

  @computed('session.option_field.booking_settings.allow_self_reschedule')
  get allowSelfReschedule() {
    return !!this.session.option_field?.booking_settings?.allow_self_reschedule;
  }

  @computed('session.option_field.booking_settings.allow_self_cancel')
  get allowSelfCancel() {
    return !!this.session.option_field?.booking_settings?.allow_self_cancel;
  }

  @computed('session.option_field.booking_settings.self_cancel_hours_notice')
  get cancelHoursNotice() {
    return this.session.option_field?.booking_settings?.self_cancel_hours_notice ?? 24;
  }

  @computed('session.option_field.booking_settings.self_reschedule_hours_notice')
  get rescheduleHoursNotice() {
    return this.session.option_field?.booking_settings?.self_reschedule_hours_notice ?? 24;
  }

  @computed('listEvents.[]')
  get upcomingEvents() {
    const now = new Date();
    return (this.listEvents || []).filter((e) => new Date(e.start_date) > now);
  }

  @computed('listEvents.[]')
  get pastEvents() {
    const now = new Date();
    return (this.listEvents || []).filter((e) => new Date(e.start_date) <= now);
  }

  @computed('upcomingEvents.[]', 'cancelHoursNotice', 'rescheduleHoursNotice')
  get upcomingEventsEnriched() {
    const now = moment();
    return this.upcomingEvents.map((e) => {
      const hoursUntil = moment(e.start_date).diff(now, 'hours');
      return {
        event: e,
        cancelDisabled: hoursUntil < this.cancelHoursNotice,
        rescheduleDisabled: hoursUntil < this.rescheduleHoursNotice,
      };
    });
  }

  @computed('session.option_field.booking_settings.hide_event_list')
  get hideEvent() {
    if (this.session.option_field?.booking_settings?.hide_event_list) {
      return this.session.option_field?.booking_settings?.hide_event_list;
    }

    return false;
  }

  @computed('session.option_field.booking_settings.hide_invoice_list')
  get hideInvoice() {
    if (this.session.option_field?.booking_settings?.hide_invoice_list) {
      return this.session.option_field?.booking_settings?.hide_invoice_list;
    }

    return false;
  }

  @computed('session.option_field.booking_settings.hide_receipt_list')
  get hideReceipt() {
    if (this.session.option_field?.booking_settings?.hide_receipt_list) {
      return this.session.option_field?.booking_settings?.hide_receipt_list;
    }

    return false;
  }

  @(task(function* () {
    yield timeout(250);

    yield this.session.me;
    if (!this.session.me?.id) {
      return;
    }

    const redirectPage = this.session.me.get('role.redirect_page');

    if (redirectPage && getConfig(this).environment !== 'development') {
      if (redirectPage.substring(0, 4) === 'http') {
        window.location.href = redirectPage;
      } else if (!document.URL.includes(redirectPage)) {
        window.location.href = `/${redirectPage}`;
      }
    }
  }).restartable())
  checkHomeTask;

  @(task(function* () {
    yield timeout(250);

    if (!this.session.me?.id) {
      return;
    }

    set(this, 'showTab', 'event');
    set(this, 'showMenu', false);

    try {
      let e = yield this.store.query('event', {});
      set(this, 'listEvents', e);
      return e;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return false;
    }
  }).restartable())
  loadEventTask;

  @(task(function* () {
    yield timeout(250);

    if (!this.session.me?.id) {
      return;
    }

    set(this, 'showTab', 'invoice');
    set(this, 'showMenu', false);

    try {
      let e = yield this.store.query('invoice', {
        document_type: 'invoice',
      });
      set(this, 'listInvoices', e);
      return e;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return false;
    }
  }).restartable())
  loadInvoiceTask;

  @(task(function* () {
    yield timeout(250);

    if (!this.session.me?.id) {
      return;
    }

    set(this, 'showTab', 'receipt');
    set(this, 'showMenu', false);

    try {
      let e = yield this.store.query('invoice', {
        document_type: 'receipt',
      });
      set(this, 'listReceipts', e);
      return e;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return false;
    }
  }).restartable())
  loadReceiptTask;

  @(task(function* (event) {
    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/cancel_booking`, this.session.token, {
        event_id: event.id,
        contact_id: this.session.me?.id,
      });

      const updated = (this.listEvents || []).filter((e) => e.id !== event.id);
      set(this, 'listEvents', updated);
      set(this, 'showCancelConfirm', null);
      this.notify.success(this.intl.t('save done'));
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
    }
  }).drop())
  cancelEventTask;

  @(task(function* (event, newSlot) {
    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/reschedule_booking`, this.session.token, {
        event_id: event.id,
        contact_id: this.session.me?.id,
        new_from_date: newSlot.start,
        new_to_date: newSlot.end,
        stanza_id: newSlot.stanza_id || null,
      });

      set(this, 'selectedEventForReschedule', null);
      this.loadEventTask.perform();
      this.notify.success(this.intl.t('save done'));
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
    }
  }).drop())
  rescheduleEventTask;

  @(task(function* () {
    yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/../logout?access_token=${this.session.token}`);

    if (this.socket) {
      this.socket.emit('logout', {
        user: this.content?.me?.id,
        token: this.session.token,
      });
    }

    this.session.logout();
    this.store.unloadAll();

    this.router.transitionTo('index');
    yield timeout(100);
    window.location.reload(true);
  }).drop())
  logoutTask;
}
